import { CommissionStatuses } from 'constants/commissionStatuses';

import { Card } from '@mui/material';
import { useUpdateCommissionsStatus } from 'hooks/queries/commissions/useUpdateCommissionsStatus';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs/Dialog/Dialog';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { TextInput } from 'shared/react-admin/Inputs';
import { Commission } from 'types/embedded/commission';
import { getId } from 'utils/getId';
import { truncateString } from 'utils/truncateString';

type Props = {
  record: Commission | undefined;
} & DialogProps;

type FormValues = {
  comment: string;
};

export const CommissionsCancelDialog: React.FC<Props> = ({ record, ...restProps }) => {
  const translate = useTypedTranslate();
  const form = useForm<FormValues>();
  const notify = useNotify();
  const { mutateAsync: updateCommissionsStatus } = useUpdateCommissionsStatus();
  const { isSubmitting } = form.formState;

  const onClose = () => {
    form.reset();
    restProps.onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      await updateCommissionsStatus({
        id: getId(record?.id),
        data: {
          status: CommissionStatuses.Cancelled,
          comment: values?.comment,
        },
      });
      await queryClient.invalidateQueries();
      onClose();
    } catch (error: any) {
      notify(error?.data?.errors[0]?.title, { type: 'error' });
    }
  };

  return (
    <Dialog {...restProps} fullWidth width="640px">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title={translate('app.merchantCommissions.commissionCancelDialog.title')}
              titleSize="smallBold"
            />
            <CardContent>
              <Typography>
                {translate('app.merchantCommissions.commissionCancelDialog.message1')}{' '}
                <TypographyBold fontSize={16} text={truncateString(record?.name || '', 50)} />{' '}
                {translate('app.merchantCommissions.commissionCancelDialog.message2')}
                <br />
                <br />
                {translate('app.merchantCommissions.commissionCancelDialog.message3')}
              </Typography>
              <Box sx={{ marginTop: '24px' }}>
                <TextInput
                  fullWidth={true}
                  helperText={false}
                  label={translate('app.table.comment')}
                  multiline={true}
                  resettable
                  size="small"
                  source="comment"
                  variant="outlined"
                />
              </Box>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton
                  key="back-button"
                  label={translate('app.buttons.cancel')}
                  onClick={onClose}
                  variant="outlined"
                />,
                <Button
                  color="error"
                  isLoading={isSubmitting}
                  key="ok-button"
                  label={translate('app.buttons.annul')}
                  type="submit"
                  variant="contained"
                />,
              ]}
            />
          </Card>
        </form>
      </FormProvider>
    </Dialog>
  );
};
